/**
 * Chiamate alle API degli ambienti di un progetto ospitato (ADR 0050 §2, §4, §5):
 * demo, produzione e test, i rilasci e i domini dell'utente sulla produzione.
 *
 * La lettura degli ambienti arriva con quella dell'hosting
 * (`GET …/hosting` → `{hosting, environments}`, in `AgentsApi.getHosting`): qui stanno le
 * scritture e le letture che servono solo alla pagina Hosting.
 *
 * Provvisorio dichiarato, come gli altri client (CLAUDE.md §2.3): percorsi e tipi scritti
 * a mano, presidiati da `environments.test.ts`.
 */

import type { HostingEnvelope, HostingState, HostingWorkload, SiteSleepState } from '../agentTypes';
import type { ApiClient } from '../client';
import type {
  MonitoringEnvironment,
  MonitoringLogLevel,
  MonitoringLogRange,
  MonitoringLogs,
  MonitoringRange,
  SiteMonitoring,
} from './monitoringTypes';

/** Nome di un ambiente. */
export type EnvironmentName = 'demo' | 'prod' | 'test';

/** Ambiente su cui agiscono «Spegni», «Riaccendi» e la riaccensione del sito in pausa. */
export type SiteEnvironment = 'demo' | 'prod';

/** Un ambiente del progetto. */
export interface HostingEnvironment {
  /** Nome dell'ambiente. */
  name: EnvironmentName;
  /** Namespace dell'ambiente (`prj-<ambiente>-<id progetto>`). */
  namespace: string;
  /**
   * Indirizzo. Per la demo è l'host privato: senza un link della demo risponde con la
   * pagina «demo privata», quindi la pagina non lo rende cliccabile. Per il test è
   * l'indirizzo interno, mai pubblicato.
   */
  url: string;
  /** Stato dell'ambiente. */
  state: HostingState;
  /** Motivo di un fallimento o nota della piattaforma; vuoto se non c'è. */
  detail: string;
  /** Sito acceso, in pausa, in riaccensione o spento; assente per il test. */
  sleep_state?: SiteSleepState | null;
  /** True se l'ambiente si apre solo con un link (la demo privata). */
  private: boolean;
  /**
   * True per la demo resa visibile a tutti (ADR 0062 §10): si apre senza link, dal browser e
   * dalle app. Solo con un piano che comprende la produzione. Assente nelle risposte di prima.
   */
  public?: boolean;
  /** True se la produzione è sospesa perché il piano non la comprende più (ADR 0050 §2.4). */
  suspended: boolean;
  /** Ruolo degli agenti dei set in scrittura: `writer`, `reader` o nessuno. */
  role: 'writer' | 'reader' | null;
  /** True se il certificato è stato emesso. */
  certificate_ready: boolean;
  /** Carichi di lavoro nel namespace. */
  workloads: HostingWorkload[];
  /** Creazione, ISO 8601. */
  created_at: string;
  /** Scadenza, ISO 8601: solo per il test, `null` altrove. */
  expires_at: string | null;
}

/** Stato di una finestra di rilascio. */
export type ReleaseState = 'open' | 'done' | 'aborted' | 'expired';

/** Una finestra di rilascio della produzione (ADR 0050 §6.4). */
export interface HostingRelease {
  /** Identificativo. */
  id: string;
  /** Stato. */
  state: ReleaseState;
  /** Riassunto del rilascio, scritto da un agente: testo da mostrare come dato. */
  summary: string;
  /** Verifica e2e su cui si è aperta la finestra; `null` se non nota. */
  test_run_id: string | null;
  /** Etichetta di chi l'ha aperta. */
  opened_by: string;
  /** Apertura, ISO 8601. */
  opened_at: string;
  /** Scadenza della finestra, ISO 8601. */
  expires_at: string;
  /** Chiusura, ISO 8601; `null` se ancora aperta. */
  closed_at: string | null;
  /** Versione rilasciata; vuota se non dichiarata. */
  version: string;
  /** Nota di chiusura, scritta da un agente. */
  note: string;
}

/** Ambienti del progetto, come li restituisce `GET …/hosting`. */
export interface HostingEnvironments {
  /** Demo; `null` se non c'è ancora. */
  demo: HostingEnvironment | null;
  /** Produzione; `null` se non attiva. */
  prod: HostingEnvironment | null;
  /** Ambiente di test; `null` se non attivo. */
  test: HostingEnvironment | null;
  /** True se il piano del proprietario ammette la produzione; `null` se non leggibile. */
  production_allowed: boolean | null;
  /** Finestra di rilascio aperta o ultima; `null` se non ce n'è. */
  release: HostingRelease | null;
}

/** Stato di un dominio dell'utente sulla produzione (ADR 0050 §5). */
export type CustomDomainState = 'pending_dns' | 'verified' | 'issuing' | 'ready' | 'failed' | 'expired' | 'dns_lost';

/** Un dominio dell'utente sulla produzione. */
export interface CustomDomain {
  /** Identificativo. */
  id: string;
  /** Nome del dominio. */
  name: string;
  /** Stato. */
  state: CustomDomainState;
  /** Nota della piattaforma (motivo di un fallimento); vuota se non c'è. */
  detail: string;
  /** Nome del record TXT da creare (`_institor-verify.<dominio>`). */
  verify_name: string;
  /** Valore del record TXT. */
  verify_value: string;
  /** Tipo del record che porta il traffico: `CNAME` o, per un dominio di secondo livello, `A`. */
  target_type: 'CNAME' | 'A';
  /** Valore del record: nome di destinazione o indirizzo IP. */
  target_value: string;
  /** True se è un dominio di secondo livello (senza sottodominio). */
  apex: boolean;
  /** Aggiunta, ISO 8601. */
  created_at: string;
  /** Ultimo controllo del DNS, ISO 8601; `null` se mai. */
  checked_at: string | null;
  /** Certificato emesso e sito raggiungibile, ISO 8601; `null` se non ancora. */
  ready_at: string | null;
}

/** Domini dell'utente sulla produzione. */
export interface CustomDomains {
  /** Domini. */
  domains: CustomDomain[];
  /** Domini ammessi dal piano; `null` se illimitati. */
  limit: number | null;
  /** Nome di destinazione per i CNAME. */
  target: string;
  /** Indirizzo dell'ingresso per i record A. */
  ingress_ip: string;
}

/** Codici dei rifiuti della produzione e dei domini, tradotti dalla pagina. */
export const PRODUCTION_REFUSAL_CODES = [
  'plan_no_production',
  'plan_unknown',
  'demo_required',
  'hosting_exists',
  'read_only_project',
  'analysis_not_approved',
  'hosting_not_allowed',
  'domain_taken',
  'plan_limit_domains',
  'production_not_ready',
  'invalid_domain',
  'domain_reserved',
] as const;

/**
 * Percorso delle rotte dell'hosting di un progetto del set.
 *
 * @param setId Set.
 * @param projectId Progetto.
 * @param suffix Parte finale.
 * @returns Il percorso.
 */
function hostingPath(setId: string, projectId: string, suffix = ''): string {
  return `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/hosting${suffix}`;
}

/** Operazioni sugli ambienti, esposte alla pagina Hosting. */
export class HostingEnvironmentsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Attiva la produzione del progetto: un sito pubblico vuoto, che si popola con il primo
   * rilascio.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Gli ambienti dopo l'attivazione.
   * @throws ApiError 409 `plan_no_production`, `plan_unknown`, `demo_required`,
   *   `hosting_exists`, `read_only_project`, `analysis_not_approved`; 422 `hosting_not_allowed`.
   */
  activateProduction(setId: string, projectId: string): Promise<HostingEnvironments> {
    return this.client.request<HostingEnvironments>('POST', hostingPath(setId, projectId, '/production'));
  }

  /**
   * Elimina la produzione: namespace, domini e tutto ciò che contiene.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param confirmHostname Nome host della produzione, riscritto dall'utente.
   * @returns Nulla; lo stato si rilegge con `getHosting`.
   */
  deleteProduction(setId: string, projectId: string, confirmHostname: string): Promise<void> {
    return this.client.request<void>('DELETE', hostingPath(setId, projectId, '/production'), {
      confirm_hostname: confirmHostname,
    });
  }

  /**
   * Rende la demo visibile a tutti o di nuovo privata (ADR 0062 §10).
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param visible True per aprirla a tutti, false per renderla privata.
   * @returns Gli ambienti dopo il cambio.
   * @throws ApiError 409 `plan_no_production` (piano Free) o `plan_unknown`.
   */
  setDemoVisibility(setId: string, projectId: string, visible: boolean): Promise<HostingEnvironments> {
    return this.client.request<HostingEnvironments>('PUT', hostingPath(setId, projectId, '/demo-visibility'), {
      public: visible,
    });
  }

  /**
   * Legge le finestre di rilascio della produzione, dalla più recente.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns I rilasci.
   */
  async listReleases(setId: string, projectId: string): Promise<HostingRelease[]> {
    const page = await this.client.request<{ releases: HostingRelease[] }>('GET', hostingPath(setId, projectId, '/releases'));
    return page.releases;
  }

  /**
   * Riaccende il sito in pausa di un ambiente.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param environment Ambiente.
   * @returns L'hosting con gli ambienti.
   */
  wake(setId: string, projectId: string, environment: SiteEnvironment): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', hostingPath(setId, projectId, `/wake?environment=${environment}`));
  }

  /**
   * Spegne il sito di un ambiente per scelta del proprietario.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param environment Ambiente.
   * @returns L'hosting con gli ambienti.
   */
  disable(setId: string, projectId: string, environment: SiteEnvironment): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', hostingPath(setId, projectId, `/disable?environment=${environment}`));
  }

  /**
   * Riaccende il sito spento di un ambiente.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param environment Ambiente.
   * @returns L'hosting con gli ambienti.
   */
  enable(setId: string, projectId: string, environment: SiteEnvironment): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', hostingPath(setId, projectId, `/enable?environment=${environment}`));
  }

  /**
   * Legge i domini dell'utente sulla produzione.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Domini, limite del piano e destinazioni dei record.
   */
  listDomains(setId: string, projectId: string): Promise<CustomDomains> {
    return this.client.request<CustomDomains>('GET', hostingPath(setId, projectId, '/production/domains'));
  }

  /**
   * Aggiunge un dominio alla produzione.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param name Nome del dominio.
   * @returns Il dominio, in attesa dei record DNS.
   * @throws ApiError 409 `domain_taken`, `plan_limit_domains`, `production_not_ready`;
   *   422 `invalid_domain`, `domain_reserved`.
   */
  addDomain(setId: string, projectId: string, name: string): Promise<CustomDomain> {
    return this.client.request<CustomDomain>('POST', hostingPath(setId, projectId, '/production/domains'), { name });
  }

  /**
   * Controlla subito i record DNS di un dominio.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param domainId Dominio.
   * @returns Il dominio dopo il controllo.
   */
  checkDomain(setId: string, projectId: string, domainId: string): Promise<CustomDomain> {
    return this.client.request<CustomDomain>(
      'POST',
      hostingPath(setId, projectId, `/production/domains/${encodeURIComponent(domainId)}/check`),
    );
  }

  /**
   * Toglie un dominio dalla produzione.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param domainId Dominio.
   * @returns Nulla.
   */
  removeDomain(setId: string, projectId: string, domainId: string): Promise<void> {
    return this.client.request<void>(
      'DELETE',
      hostingPath(setId, projectId, `/production/domains/${encodeURIComponent(domainId)}`),
    );
  }
  /**
   * Legge le metriche di un ambiente del sito (ADR 0056): pod, traffico, volumi e avvisi.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param environment Ambiente.
   * @param range Periodo.
   * @returns Il monitoraggio del periodo.
   * @throws ApiError 409 `plan_no_monitoring` (piano Free); 404 se l'ambiente non c'è;
   *   503 `monitoring_unavailable` se il servizio delle metriche non risponde.
   */
  getMonitoring(
    setId: string,
    projectId: string,
    environment: MonitoringEnvironment,
    range: MonitoringRange,
  ): Promise<SiteMonitoring> {
    return this.client.request<SiteMonitoring>(
      'GET',
      hostingPath(setId, projectId, `/monitoring?environment=${environment}&range=${range}`),
    );
  }

  /**
   * Legge i log di un ambiente del sito, già ripuliti dal server. Solo per chi amministra
   * il set.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param environment Ambiente.
   * @param range Periodo, al più 72 ore.
   * @param level `errors` per i soli errori, `all` per tutte le righe.
   * @returns Le righe e le ore di conservazione.
   * @throws ApiError 403 o 404 per chi non amministra il set.
   */
  getMonitoringLogs(
    setId: string,
    projectId: string,
    environment: MonitoringEnvironment,
    range: MonitoringLogRange,
    level: MonitoringLogLevel,
  ): Promise<MonitoringLogs> {
    return this.client.request<MonitoringLogs>(
      'GET',
      hostingPath(setId, projectId, `/monitoring/logs?environment=${environment}&range=${range}&level=${level}`),
    );
  }
}
